import React, { useState, useEffect, ChangeEvent, ReactNode, CSSProperties } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../types/admin';

// Custom hook to detect mobile breakpoint
const useIsMobile = (breakpoint = 768): boolean => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

interface FilterOption {
  value: string;
  label: string;
}

interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: string;
  filterOptions?: FilterOption[];
  width?: string;
  render?: (value: unknown, row: DataRow) => ReactNode;
}

interface DataRow {
  id?: string | number;
  isOverdue?: boolean;
  [key: string]: unknown;
}

interface FilterTab {
  key: string;
  label: string;
  count?: number;
  variant?: string;
}

interface FilterTabsConfig {
  tabs: FilterTab[];
  activeTab: string;
  onTabChange: (tab: string) => void;
}

interface SkeletonRowProps {
  columns: TableColumn[];
  showCheckboxes: boolean;
  brand: BrandConfig;
}

// Skeleton row component with shimmer animation
const SkeletonRow: React.FC<SkeletonRowProps> = ({ columns, showCheckboxes, brand }) => (
  <tr className="border-b animate-pulse" style={{ borderColor: brand.colors.border }}>
    {showCheckboxes && (
      <td className="px-4 py-3">
        <div className="h-4 w-4 rounded-sm" style={{ backgroundColor: brand.colors.border }} />
      </td>
    )}
    {columns.map((col, idx) => (
      // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO(deps-2026-07): newly detected by @eslint-react bump, burn-down separately
      <td key={col.key || idx} className="px-4 py-3">
        <div className="space-y-2">
          <div
            className="h-4 rounded-sm"
            style={{
              backgroundColor: brand.colors.border,
              width: col.key === 'name' ? '70%' : col.key === 'actions' ? '60%' : '50%'
            }}
          />
          {(col.key === 'name' || col.key === 'appMetadata') && (
            <div
              className="h-3 rounded-sm"
              style={{ backgroundColor: brand.colors.border, width: '40%' }}
            />
          )}
        </div>
      </td>
    ))}
  </tr>
);

interface SkeletonCardProps {
  brand: BrandConfig;
}

// Mobile skeleton card component
const SkeletonCard: React.FC<SkeletonCardProps> = ({ brand }) => (
  <div
    className="p-4 border-b animate-pulse"
    style={{ borderColor: brand.colors.border }}
  >
    <div className="space-y-3">
      <div className="h-5 rounded-sm w-3/4" style={{ backgroundColor: brand.colors.border }} />
      <div className="h-4 rounded-sm w-1/2" style={{ backgroundColor: brand.colors.border }} />
      <div className="h-4 rounded-sm w-2/3" style={{ backgroundColor: brand.colors.border }} />
    </div>
  </div>
);

interface MobileDataCardProps {
  row: DataRow;
  columns: TableColumn[];
  showCheckboxes: boolean;
  isSelected: boolean;
  rowAriaLabel: string;
  onSelect: () => void;
  onRowClick?: (row: DataRow) => void;
  brand: BrandConfig;
  getRowStyle?: (row: DataRow) => CSSProperties;
}

// Mobile card component for a single data row
const MobileDataCard: React.FC<MobileDataCardProps> = ({ row, columns, showCheckboxes, isSelected, rowAriaLabel, onSelect, onRowClick, brand, getRowStyle }) => {
  // Get first column as the primary/title field
  const primaryColumn = columns[0];
  const secondaryColumns = columns.slice(1);

  return (
    <div
      className={`p-4 border-b transition-colors ${onRowClick ? 'cursor-pointer active:bg-gray-100' : ''} ${isSelected ? 'bg-blue-50' : ''} ${row.isOverdue ? 'bg-red-50' : ''}`}
      style={{ borderColor: brand.colors.border, ...(getRowStyle?.(row) || {}) }}
      {...(onRowClick ? { role: 'button', tabIndex: 0, onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } } : {})}
      onClick={() => onRowClick?.(row)}
    >
      <div className="flex items-start gap-3">
        {showCheckboxes && (
          <input
            type="checkbox"
            aria-label={rowAriaLabel}
            checked={isSelected}
            onChange={onSelect}
            onClick={(e) => e.stopPropagation()}
            className="rounded-sm mt-1 min-w-[20px] min-h-[20px]"
          />
        )}
        <div className="flex-1 min-w-0">
          {/* Primary field (first column) */}
          <div className="font-medium text-base mb-2" style={{ color: brand.colors.text }}>
            {primaryColumn.render
              ? primaryColumn.render(row[primaryColumn.key], row)
              : row[primaryColumn.key] as ReactNode}
          </div>
          {/* Secondary fields */}
          <div className="space-y-1.5">
            {secondaryColumns.map(col => (
              <div key={col.key} className="flex items-start text-sm">
                <span className="text-xs font-medium uppercase w-24 shrink-0" style={{ color: brand.colors.textLight }}>
                  {col.label}:
                </span>
                <span style={{ color: brand.colors.text }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key] as ReactNode}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface AdminDataTableProps {
  columns: TableColumn[];
  data: DataRow[];
  totalCount?: number;
  showCheckboxes?: boolean;
  showPagination?: boolean;
  filterTabs?: FilterTabsConfig | null;
  onRowClick?: (row: DataRow) => void;
  brand: BrandConfig;
  Icons?: IconsType;
  isLoading?: boolean;
  skeletonRows?: number;
  getRowStyle?: (row: DataRow) => CSSProperties;
}

const AdminDataTable: React.FC<AdminDataTableProps> = ({
  columns,
  data,
  totalCount: propTotalCount,
  showCheckboxes = false,
  showPagination = true,
  filterTabs = null,
  onRowClick,
  brand,
  Icons,
  isLoading = false,
  skeletonRows = 5,
  getRowStyle,
}) => {
  // Default totalCount to data length if not provided
  const totalCount = propTotalCount ?? data?.length ?? 0;
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const isMobile = useIsMobile();

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(data.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (index: number) => {
    setSelectedRows(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  };

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="rounded-xl shadow-xs overflow-hidden" style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}>
      {/* Header Row with count and filter tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-3 border-b gap-3" style={{ borderColor: brand.colors.border }}>
        <div className="flex items-center gap-2">
          <span className="text-xl sm:text-2xl font-semibold" style={{ color: brand.colors.link }}>{totalCount}</span>
          <span className="text-sm" style={{ color: brand.colors.textLight }}>
            {filterTabs?.activeTab || 'Items'}
          </span>
        </div>

        {filterTabs && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0">
            {filterTabs.tabs.map(tab => (
              <button
                key={tab.key}
                className="px-3 py-2 sm:py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap min-h-[36px] sm:min-h-0"
                onClick={() => filterTabs.onTabChange(tab.key)}
                style={{
                  backgroundColor: filterTabs.activeTab === tab.key
                    ? (tab.variant === 'warning' ? brand.colors.warning : brand.colors.primary)
                    : brand.colors.bgLight,
                  color: filterTabs.activeTab === tab.key
                    ? (tab.variant === 'warning' ? 'white' : brand.colors.onPrimary)
                    : brand.colors.textLight,
                }}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-1">({tab.count})</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div>
          {/* Mobile select all checkbox */}
          {showCheckboxes && data.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}>
              <input
                type="checkbox"
                aria-label="Select all rows"
                onChange={handleSelectAll}
                checked={selectedRows.length === data.length && data.length > 0}
                className="rounded-sm min-w-[20px] min-h-[20px]"
              />
              <span className="text-sm" style={{ color: brand.colors.textLight }}>Select all</span>
            </div>
          )}

          {isLoading ? (
            [...Array(skeletonRows)].map((_, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton placeholders
              <SkeletonCard key={`skeleton-${index}`} brand={brand} />
            ))
          ) : (
            data.map((row, index) => (
              <MobileDataCard
                // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO(deps-2026-07): newly detected by @eslint-react bump, burn-down separately
                key={row.id || index}
                row={row}
                columns={columns}
                showCheckboxes={showCheckboxes}
                isSelected={selectedRows.includes(index)}
                rowAriaLabel={`Select row ${index + 1}`}
                onSelect={() => handleSelectRow(index)}
                onRowClick={onRowClick}
                brand={brand}
                getRowStyle={getRowStyle}
              />
            ))
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {/* Column Headers */}
              <tr style={{ backgroundColor: brand.colors.bgLight }}>
                {showCheckboxes && (
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      onChange={handleSelectAll}
                      checked={selectedRows.length === data.length && data.length > 0}
                      className="rounded-sm"
                    />
                  </th>
                )}
                {columns.map(col => (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                    style={{
                      color: brand.colors.textLight,
                      width: col.width
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {col.sortable && Icons?.ChevronDown && (
                        <span className="flex flex-col">
                          <Icons.ChevronDown
                            className={`w-3 h-3 -mb-1 transform rotate-180 ${sortColumn === col.key && sortDirection === 'asc' ? 'opacity-100' : 'opacity-30'}`}
                          />
                          <Icons.ChevronDown
                            className={`w-3 h-3 ${sortColumn === col.key && sortDirection === 'desc' ? 'opacity-100' : 'opacity-30'}`}
                          />
                        </span>
                      )}
                      {col.sortable && !Icons?.ChevronDown && (
                        <span className="ml-1 text-xs">
                          {sortColumn === col.key ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>

              {/* Filter Row */}
              <tr style={{ backgroundColor: brand.colors.bgLight, borderTop: `1px solid ${brand.colors.border}` }}>
                {showCheckboxes && <th className="px-4 py-2"></th>}
                {columns.map(col => {
                  const filterId = `admin-data-table-filter-${col.key}`;
                  return (
                    <th key={`filter-${col.key}`} className="px-4 py-2">
                      {col.filterable && (
                        col.filterType === 'select' ? (
                          <>
                            <label htmlFor={filterId} className="sr-only">Filter by {col.label}</label>
                            <select
                              id={filterId}
                              value={columnFilters[col.key] || ''}
                              onChange={(e) => handleColumnFilter(col.key, e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded-sm"
                              style={{ borderColor: brand.colors.border }}
                            >
                              <option value="">All</option>
                              {col.filterOptions?.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </>
                        ) : (
                          <>
                            <label htmlFor={filterId} className="sr-only">Filter by {col.label}</label>
                            <input
                              id={filterId}
                              type="text"
                              placeholder={col.label}
                              value={columnFilters[col.key] || ''}
                              onChange={(e) => handleColumnFilter(col.key, e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded-sm"
                              style={{ borderColor: brand.colors.border }}
                            />
                          </>
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                // Show skeleton rows when loading
                [...Array(skeletonRows)].map((_, index) => (
                  <SkeletonRow
                    // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton placeholders
                    key={`skeleton-${index}`}
                    columns={columns}
                    showCheckboxes={showCheckboxes}
                    brand={brand}
                  />
                ))
              ) : (
                data.map((row, index) => (
                  <tr
                    // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO(deps-2026-07): newly detected by @eslint-react bump, burn-down separately
                    key={row.id || index}
                    className={`
                      border-b transition-colors cursor-pointer
                      ${selectedRows.includes(index) ? 'bg-blue-50' : 'hover:bg-gray-50'}
                      ${row.isOverdue ? 'bg-red-50' : ''}
                    `}
                    style={{ borderColor: brand.colors.border, ...(getRowStyle?.(row) || {}) }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {showCheckboxes && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Select row ${index + 1}`}
                          checked={selectedRows.includes(index)}
                          onChange={() => handleSelectRow(index)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-sm"
                        />
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col.key} className="px-4 py-3 text-sm" style={{ color: brand.colors.text }}>
                        {col.render ? col.render(row[col.key], row) : row[col.key] as ReactNode}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {showPagination && (
        <div className="border-t" style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}>
          {/* Mobile Pagination */}
          <div className="md:hidden px-3 py-3 space-y-3">
            <div className="flex items-center justify-between text-sm" style={{ color: brand.colors.textLight }}>
              <span>
                {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-2">
                <label htmlFor="admin-data-table-page-size-mobile">Show</label>
                <select
                  id="admin-data-table-page-size-mobile"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-2 py-2 border rounded-sm min-h-touch"
                  style={{ borderColor: brand.colors.border }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="flex-1 px-3 py-3 text-sm border rounded-sm disabled:opacity-50 min-h-touch"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </button>
              <button
                className="flex-1 px-3 py-3 text-sm border rounded-sm disabled:opacity-50 min-h-touch"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>

          {/* Desktop Pagination */}
          <div className="hidden md:flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-sm" style={{ color: brand.colors.textLight }}>
              <label htmlFor="admin-data-table-page-size-desktop">Show</label>
              <select
                id="admin-data-table-page-size-desktop"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 border rounded-sm"
                style={{ borderColor: brand.colors.border }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
              </select>
              <span>entries</span>
            </div>

            <div className="text-sm" style={{ color: brand.colors.textLight }}>
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
            </div>

            <div className="flex gap-1">
              <button
                className="px-3 py-1 text-sm border rounded-sm disabled:opacity-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </button>
              <button
                className="px-3 py-1 text-sm border rounded-sm disabled:opacity-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className="px-3 py-1 text-sm border rounded-sm"
                    style={{
                      borderColor: brand.colors.border,
                      backgroundColor: currentPage === pageNum ? brand.colors.primary : 'white',
                      color: currentPage === pageNum ? brand.colors.onPrimary : brand.colors.text,
                    }}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className="px-3 py-1 text-sm border rounded-sm disabled:opacity-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
              <button
                className="px-3 py-1 text-sm border rounded-sm disabled:opacity-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDataTable;
